import { Link } from 'react-router-dom';
import { Card } from '@thegpvc/ui';
import { text, cx } from '../../styles/theme';
import { Eyebrow } from './WorkDeliveredCard';
import type { GetNeedsALookResponse } from '../../types/api';

const KIND_STYLES: Record<string, string> = {
  stalled: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-300',
  anomalous: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300',
  circular: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300',
};

const KIND_LABELS: Record<string, string> = {
  stalled: 'Stalled',
  anomalous: 'Anomalous',
  circular: 'Circular',
};

/**
 * NeedsALookCard answers "what needs me?": stalled, anomalous, and circular
 * sessions with server-formatted reasons, each linking into the session.
 *
 * Each row headlines the session by its AI title (so two flagged sessions in
 * the same project are distinguishable), falling back to the linked short id
 * when a session is not yet summarized, with the project as secondary context.
 */
export function NeedsALookCard({ data, loading }: { data?: GetNeedsALookResponse; loading: boolean }) {
  const items = data?.items ?? [];

  return (
    <Card>
      <Card.Body>
        <div className="flex items-start justify-between">
          <Eyebrow>Needs a look</Eyebrow>
          {(data?.openCount ?? 0) > 0 && (
            <span className={cx('text-sm tabular-nums', text.secondary)}>{data?.openCount} open</span>
          )}
        </div>

        {loading && !data && (
          <div className="mt-3 space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-gray-100 dark:bg-navy-800" />
            ))}
          </div>
        )}

        {!loading && items.length === 0 && (
          <p className={cx('mt-4 py-6 text-center text-sm', text.secondary)}>
            Nothing needs your attention. Sessions that stall, loop, or go statistically weird land here.
          </p>
        )}

        {items.length > 0 && (
          <ul className="mt-3 divide-y divide-gray-100 dark:divide-navy-800">
            {items.map((item) => {
              const shortId = item.sessionId ? `${item.sessionId.slice(0, 8)}…` : 'session';
              const headline = item.title || shortId;
              return (
                <li key={item.sessionId} className="flex items-center gap-3 py-2.5">
                  <span
                    className={cx(
                      'shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium',
                      KIND_STYLES[item.kind ?? ''] ?? KIND_STYLES.circular,
                    )}
                  >
                    {KIND_LABELS[item.kind ?? ''] ?? item.kind}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/sessions/${item.sessionId}`}
                      className={cx('block truncate font-medium hover:underline', text.primary)}
                    >
                      {headline}
                    </Link>
                    <div className={cx('truncate text-xs', text.muted)}>{item.project || 'unknown project'}</div>
                  </div>
                  <span className={cx('shrink-0 text-sm', text.muted)}>{item.reason}</span>
                </li>
              );
            })}
          </ul>
        )}
      </Card.Body>
    </Card>
  );
}
